import React from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'

import { getNewsletterPosts, getNewsletterYearMonth } from '@/lib/blog/newsletter'
import { Columns, Column, Layout, Rows, Section } from '@/components/layout'
import BlogArticleListingSmall from '@/components/blocks/BlogArticleListingSmall'
import BlogArticleListingSmallHeader from '@/components/blocks/BlogArticleListingSmallHeader'
import BlogSubscribe from '@/components/blocks/BlogSubscribe'
import { Card } from '@/components/common'
import { formatBlogDate } from '@/utils/date'
import Link from '@/components/common/Link'

import styles from './newsletter.module.scss'

const pageId = 'newsletter'
const getMessageId = id => `pages.newsletter.${id}`

export default function Newsletter({ latestIssue, recentIssues }) {
  const { formatMessage } = useIntl()

  return (
    <Layout.Content>

      {/* Header */}
      <Section bgBlob textCenter>
        <h1>{formatMessage({ id: getMessageId('hero.heading') })}</h1>
        <p>{formatMessage({ id: getMessageId('hero.subheading') })}</p>
      </Section>

      {/* Latest Issue */}
      {latestIssue && (
        <Section>
          <h2>{formatMessage({ id: getMessageId('latestIssue.heading') })}</h2>
          <Card className={styles.latestCard} withShadow>
            <Columns>
              {latestIssue.featuredImage?.node?.sourceUrl && (
                <Column size={10}>
                  <img
                    alt={latestIssue.featuredImage.node.altText || latestIssue.title}
                    className={styles.latestImage}
                    src={latestIssue.featuredImage.node.sourceUrl}
                  />
                </Column>
              )}
              <Column>
                <div className={styles.latestContent}>
                  <h3 className={styles.latestTitle}>{latestIssue.title}</h3>
                  <div className={styles.latestExcerpt} dangerouslySetInnerHTML={{ __html: latestIssue.excerpt }} />
                  <Link to={`/newsletter/archive/${getNewsletterYearMonth(latestIssue)}`}>
                    {formatMessage({ id: getMessageId('latestIssue.cta') })}
                  </Link>
                </div>
              </Column>
            </Columns>
          </Card>
        </Section>
      )}

      {/* Past Issues + Cross Links + Subscribe */}
      <Section>
        <Columns>
          <Column>
            <Rows gap={0}>
              <BlogArticleListingSmallHeader title={formatMessage({ id: getMessageId('archivePreview.heading') })} />
              {recentIssues.map((issue, index) => (
                <BlogArticleListingSmall
                  key={issue.slug}
                  date={formatBlogDate(new Date(issue.date))}
                  id={`newsletter-past-${index}`}
                  img={issue.featuredImage?.node?.sourceUrl}
                  imgAlt={issue.featuredImage?.node?.altText}
                  link={`/newsletter/archive/${getNewsletterYearMonth(issue)}`}
                  title={issue.title}
                />
              ))}
              <Link to="/newsletter/archive">
                {formatMessage({ id: getMessageId('archivePreview.cta') })}
              </Link>
            </Rows>
          </Column>
          <Column>
            <BlogSubscribe />
          </Column>
        </Columns>
      </Section>

    </Layout.Content>
  )
}

const issuePropType = PropTypes.shape({
  slug: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  excerpt: PropTypes.string,
  title: PropTypes.string,
  featuredImage: PropTypes.shape({
    node: PropTypes.shape({
      sourceUrl: PropTypes.string,
      altText: PropTypes.string,
    }),
  }),
})

Newsletter.propTypes = {
  latestIssue: issuePropType,
  recentIssues: PropTypes.arrayOf(issuePropType).isRequired,
}

Newsletter.layoutProps = { pageId }

export async function getStaticProps() {
  const posts = await getNewsletterPosts()
  const [latestIssue, ...rest] = posts
  const recentIssues = rest.slice(0, 3)

  return {
    props: {
      latestIssue: latestIssue ?? null,
      recentIssues,
    },
    revalidate: 60 * 60,
  }
}
